<template>
  <div>
    <p class="text-sm px-4 mb-2 text-grey-575">Cover you've chosen</p>
    <div class="mb-5">
      <div v-for="product in configuredProducts" :key="product.key">
        <div v-for="rate in product.rates" :key="rate.key">
          <div
            v-if="!isMinimumPremiumAdjustment(rate)"
            class="flex items-center px-4 py-4 bg-white justify-between mb-1 text-grey-550"
          >
            <div>
              <div class="font-medium mb-1 text-blue-700" data-cy="cover-chosen">{{ getProductName(rate.key) }}</div>
              <div
                v-if="
                  (product.key === 'CommercialCombined' || product.key === 'AvivaCommercialCombined') &&
                    (rate.key === 'CommercialCombined\\OfficeCover' ||
                    rate.key === 'AvivaCommercialCombined\\OfficeCover')
                "
              >
                <div v-if="rate.ratingInputs.portablePropertyLimit" class="text-sm">
                  {{ rate.ratingInputs.portablePropertyLimit | currency }}
                  electronic equipment
                  {{ rate.ratingInputs.portablePropertyJurisdiction.toLowerCase() }}
                </div>
                <div v-if="rate.ratingInputs.fixedPropertyLimit > 0" class="text-sm ">
                  {{ rate.ratingInputs.fixedPropertyLimit | currency }} contents at your premises
                </div>
                <div v-if="rate.ratingInputs.revenueProtectionLimit" class="text-sm ">
                  {{ rate.ratingInputs.revenueProtectionLimit | currency }}
                  increased cost of working
                </div>
                <div v-if="rate.ratingInputs.moneyAssaultActive" class="text-sm ">
                  £2,500 money and assault
                </div>
                <div v-if="isConfirmation" class="text-sm ">
                  {{ rate.ratingAttributes.portable_property_excess | currency }} excess
                </div>
              </div>
              <div
                v-if="
                  (product.key === 'CommercialCombined' || product.key === 'AvivaCommercialCombined') &&
                    (rate.key === 'CommercialCombined\\GeneralLiability' ||
                    rate.key === 'AvivaCommercialCombined\\GeneralLiability')
                "
              >
                <div class="text-sm">
                  {{ rate.ratingInputs.publicLiabilityLimit | currency }} public and products liability
                </div>
                <div v-if="rate.ratingInputs.employersLiabilityLimit > 0" class="text-sm ">
                  {{ rate.ratingInputs.employersLiabilityLimit | currency }}
                  employers liability
                </div>
                <div v-if="isConfirmation" class="text-sm ">
                  £250 excess (only applies to property damage)
                </div>
              </div>
              <div
                v-if="
                  (product.key === 'CommercialCombined' || product.key === 'AvivaCommercialCombined') &&
                    (rate.key === 'CommercialCombined\\EmployersLiability' ||
                    rate.key === 'AvivaCommercialCombined\\EmployersLiability')
                "
              >
                <div class="text-sm ">
                  {{ rate.ratingInputs.limit | currency }}
                  employers liability
                </div>
              </div>
              <div
                v-if="
                  (product.key === 'CommercialCombined' || product.key === 'AvivaCommercialCombined') &&
                    (rate.key === 'CommercialCombined\\PublicLiability' ||
                    rate.key === 'AvivaCommercialCombined\\PublicLiability')
                "
              >
                <div class="text-sm">
                  {{ rate.ratingInputs.limit | currency }} public and products liability
                </div>
                <div v-if="isConfirmation" class="text-sm ">
                  £250 excess (only applies to property damage)
                </div>
              </div>
              <div v-if="rate.key === 'CommercialCombined\\AppearHere'">
                <div class="text-sm "> {{ formatDate(rate.ratingInputs.startDate) }} - {{ formatDate(rate.ratingInputs.endDate) }}</div>
              </div>
              <div v-if="product.key === 'ProfessionalIndemnity'">
                <div class="text-sm ">
                  {{ rate.ratingInputs.limit | currency }} limit each claim
                  <div v-if="!rate.ratingInputs.coverPastWork" class="text-sm ">
                    Doesn't cover services provided before {{ startDate }}
                  </div>
                </div>
                <div v-if="isConfirmation" class="text-sm ">
                  {{ getPiExcess() }}
                </div>
              </div>
              <div v-if="product.key === 'CyberSecurity'">
                <div class="text-sm "> {{ rate.ratingInputs.limit | currency }} annual limit </div>
                <div v-if="isConfirmation" class="text-sm ">
                  £2,500 excess
                </div>
              </div>
              <div v-if="product.key === 'LegalExpenses'">
                <div class="text-sm "> {{ rate.ratingInputs.limit | currency }} limit each claim </div>
                <div v-if="isConfirmation" class="text-sm ">
                  No excess applies
                </div>
              </div>
              <div v-if="product.key === 'ManagementLiability'">
                <div class="text-sm "> {{ rate.ratingInputs.limit | currency }} limit each single event </div>
                <div v-if="isConfirmation">
                  <div class="text-sm ">
                    {{
                      rate.key === 'ManagementLiability\\DirectorsAndOfficers'
                        ? '£2,500 excess (only applies to claims made by the company)'
                        : '£2,500 excess'
                    }}
                  </div>
                  <div v-if="rate.key === 'ManagementLiability\\DirectorsAndOfficers'" class="text-sm ">
                    No excess applies to claims made by directors, officers or employees
                  </div>
                </div>
              </div>
              <div v-if="product.key === 'HealthcareProfessional'">
                <div class="text-2xs text-shade-400"> {{ rate.ratingInputs.limit | currency }} annual limit </div>
                <div class="text-xs text-shade-400">
                  Includes medical malpractice
                </div>
                <div class="text-xs text-shade-400">
                  Includes professional indemnity
                </div>
                <div class="text-xs text-shade-400">
                  Includes public liability
                </div>
                <div v-if="isConfirmation" class="text-2xs text-shade-400">
                  {{ rate.ratingInputs.excess | currency }} excess
                </div>
              </div>
              <div v-if="rate.key === 'ProfessionalInsurance\\CyberSecurity'">
                <div class="text-sm ">
                  {{ rate.ratingInputs.liabilityLimit | currency }} limit
                </div>
                <div class="text-sm ">
                  Plus breach response services for {{ rate.ratingInputs.notificationLimit | numberWithCommas }} notified individuals
                </div>
              </div>
              <div v-if="rate.key === 'ProfessionalInsurance\\ProfessionalIndemnity'">
                <div class="text-sm "> {{ rate.ratingInputs.limit | currency }} limit each claim </div>
              </div>
              <div v-if="isConfirmation" class="mt-1">
                <div class="h-8 flex flex-col justify-center items-start">
                  <DrDottedLoader v-if="loading === rate.key" class="inline-block" />
                  <a
                    v-else
                    href="#"
                    class="text-blue-500 text-sm no-underline"
                    @click="downloadSchedule(product, rate)"
                  >
                    See the policy wording for full terms and conditions
                  </a>
                </div>
                <a
                  v-if="getSummaryOfCoverLink(rate.key)"
                  :href="getSummaryOfCoverLink(rate.key)"
                  target="_blank" rel="noopener noreferrer"
                  class="text-blue-500 text-sm no-underline"
                >
                  Summary of cover
                </a>
              </div>
            </div>
            <RouterLink v-if="!isPortal" :to="getConfigPath(rate)" class="text-sm no-underline text-blue-500">
              Change
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isConfirmation && (removedProducts.length || ratesUnoffered.length)" class="px-6 mb-6">
      <p class="text-sm">We need to chat before we can give you a quote for</p>
      <div v-for="product in removedProducts" :key="product">
        <div class="flex items-center">
          <DrIcon svg="close" width="24" height="24" class="text-yellow-400" />
          <span data-cy="cover-removed">{{ getProductName(product) }}</span>
        </div>
      </div>
      <div v-for="product in ratesUnoffered" :key="`unoffered-${product.key}`">
        <div class="flex items-center">
          <DrIcon svg="close" width="24" height="24" class="text-yellow-400" />
          <span>{{ getProductName(product.key) }}</span>
        </div>
      </div>
    </div>

    <div v-if="isConfirmation && missingProducts.length" class="px-6 mb-6">
      <p class="text-sm">Cover you haven't chosen</p>
      <div v-for="product in missingProducts" :key="product">
        <div class="flex items-center">
          <DrIcon svg="close" width="24" height="24" class="text-red-650" />
          <span>{{ getProductName(product) }}</span>
        </div>
      </div>
    </div>
    <div v-if="missingProducts.length > 0" class="px-6">
      <RouterLink to="/products" class="no-underline w-full">
        <DrButton class="btn-block btn-blue p-3">Add more cover</DrButton>
      </RouterLink>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axiosPolicyDocsApi from '@/services/axiosPolicyDocsApi'

export default {
  props: {
    isConfirmation: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      loading: '',
      removeConfirmation: '',
      productList: [],
      ratesUnoffered: this.$store.getters.getRatesUnoffered,
    }
  },
  computed: {
    ...mapGetters(['getSource']),
    isPortal () {
      return !!this.$store.state.portal
    },
    startDate () {
      if (this.$store.getters.getEffectiveDate) {
        const startDate = new Date(this.$store.getters.getEffectiveDate)
        let month = (startDate.getMonth() + 1).toString()
        let day = startDate.getDate().toString()
        if (day.length === 1) day = `0${day}`
        if (month.length === 1) month = `0${month}`
        const year = startDate.getFullYear()
        const dateString = `${day}/${month}/${year}`
        return dateString
      }
      return 'policy start date'
    },
    configuredProducts () {
      return this.$store.getters.getProducts
    },
    configuredRates () {
      return this.configuredProducts.flatMap(item => item.rates)
    },
    missingProducts () {
      return this.availableRates.filter((availableRate) => {
        // MPA shouldn't display anywhere
        if (availableRate.includes('MinimumPremiumAdjustment')) { return false }

        // given rate is neither within array of removed rates, nor array of referred only rates, nor array of taken rates
        return this.removedProducts.find(removedRate => removedRate === availableRate) === undefined &&
          this.ratesUnoffered.find(unofferedRate => unofferedRate.key === availableRate) === undefined &&
          this.configuredRates.find(configuredRate => configuredRate.key === availableRate) === undefined
      })
    },
    removedProducts () {
      return this.$store.getters.getRemovedProducts
    },
    availableRates () {
      return this.$store.getters.getAvailableRates
    },
  },
  methods: {
    confirmAction (product) {
      this.removeConfirmation = product.key
    },
    isMinimumPremiumAdjustment (rate) {
      return rate.key.includes('MinimumPremiumAdjustment')
    },
    getPiExcess () {
      const ratingType = this.$store.getters.getPiRatingType
      const turnover = this.$store.getters.getTurnover
      let excess = 0
      if (ratingType === 'accountants' || ratingType === 'chartered_accountants') {
        excess = turnover > 100000 ? 1000 : 500
      } else {
        excess = turnover > 1000000 ? 500 : 0
      }
      return excess > 0 ? `£${excess} excess` : 'No excess applies'
    },
    getConfigPath (rate) {
      return `/products/${rate.key}/config`
    },
    getProductName (key) {
      if (key === 'CommercialCombined\\OfficeCover' || key === 'AvivaCommercialCombined\\OfficeCover') { return 'Contents and equipment' }
      if (key === 'CommercialCombined\\GeneralLiability' || key === 'AvivaCommercialCombined\\GeneralLiability') { return 'General liability' }
      if (key === 'CommercialCombined\\EmployersLiability' || key === 'AvivaCommercialCombined\\EmployersLiability') { return 'Employers liability' }
      if (key === 'CommercialCombined\\PublicLiability' || key === 'AvivaCommercialCombined\\PublicLiability') { return 'Public liability' }
      if (key === 'ProfessionalIndemnity\\ProfessionalIndemnity') return 'Professional indemnity'
      if (key === 'CyberSecurity\\CyberSecurity') return 'Cyber insurance (GDPR)'
      if (key === 'LegalExpenses\\LegalExpenses') return 'Commercial legal protection'
      if (key === 'ManagementLiability\\DirectorsAndOfficers') return 'Directors and officers (D&O)'
      if (key === 'ManagementLiability\\EmploymentPracticesLiability') return 'Employment practices'
      if (key === 'ManagementLiability\\Crime') return 'Crime'
      if (key === 'HealthcareProfessional\\HealthcareProfessional') return 'Healthcare professional'
      if (key === 'ProfessionalInsurance\\CyberSecurity') return 'Cyber insurance'
      if (key === 'ProfessionalInsurance\\ProfessionalIndemnity') return 'Professional indemnity'
      if (key === 'CommercialCombined\\AppearHere') return 'Pop up insurance'
    },
    async getPolicyURL (product) {
      const payload = {
        contact: this.$store.state.contact,
        product,
        reference: this.$store.state.reference,
        statement: this.$store.state.statement,
      }

      const { data } = await axiosPolicyDocsApi.post(`quote/policy`, payload, {
        responseType: 'blob',
      })
      const blob = new Blob([data], { type: 'application/pdf' })
      return window.URL.createObjectURL(blob)
    },
    async downloadSchedule (product, rate) {
      // Check if user is Safari
      const ua = window.navigator.userAgent.toLowerCase()
      const safari = ua.indexOf('safari') && !ua.includes('chrome')

      // Safari has built-in popup blocking enabled by default; have to open up a new
      // window and change the location after async request returns link
      if (safari) {
        const win = window.open()
        win.document.write('Loading policy document...')
        const URL = await this.getPolicyURL(product)
        win.location.href = URL
      } else {
        this.loading = rate.key
        const URL = await this.getPolicyURL(product)
        window.open(URL, '_blank')
        this.loading = ''
      }
    },
    getSummaryOfCoverLink (key) {
      if (key === 'HealthcareProfessional\\HealthcareProfessional') return 'https://docs.digitalrisks.co.uk/soc_healthcare_professional.pdf'
      if (key === 'CommercialCombined\\OfficeCover' || key === 'AvivaCommercialCombined\\OfficeCover') return 'https://docs.digitalrisks.co.uk/soc_contents_and_equipment.pdf'
      if (key === 'CommercialCombined\\GeneralLiability' || key === 'AvivaCommercialCombined\\GeneralLiability') return 'https://docs.digitalrisks.co.uk/soc_general_liability.pdf'
      if (key === 'ProfessionalInsurance\\ProfessionalIndemnity') return 'https://docs.digitalrisks.co.uk/soc_professional_indemnity.pdf'
      if (key === 'ProfessionalInsurance\\CyberSecurity' || key === 'CyberSecurity\\CyberSecurity') return 'https://docs.digitalrisks.co.uk/soc_cyber_security.pdf'
      if (key === 'LegalExpenses\\LegalExpenses') return 'https://docs.digitalrisks.co.uk/soc_commercial_legal_protection.pdf'
      if (key === 'ManagementLiability\\DirectorsAndOfficers') return 'https://docs.digitalrisks.co.uk/soc_management_liability.pdf'
      if (key === 'ManagementLiability\\EmploymentPracticesLiability') return 'https://docs.digitalrisks.co.uk/soc_employment_practices_liability.pdf'
      if (key === 'ManagementLiability\\Crime') return 'https://docs.digitalrisks.co.uk/soc_crime.pdf'
      return null
    },
    formatDate (val) {
      const date = new Date(val)
      const options = { dateStyle: 'short' }
      return date.toLocaleDateString('en-GB', options)
    },
  },
}
</script>

<style>
.slide-out-enter-active,
.slide-out-leave-active {
  transition: all 0.3s;
}

.slide-out-enter {
  opacity: 0;
  transform: translateX(100px);
}

.slide-out-leave-to {
  opacity: 0;
  transform: translateX(-100px);
}
</style>
